import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { RiCloseLine, RiArrowLeftSLine, RiArrowRightSLine, RiFireFill, RiFireLine } from 'react-icons/ri';
import { getStreakCalendar } from '../../api/quiz.api';
import { isStudiedToday } from '../../utils/streak';
import KitsuMark from '../brand/KitsuMark';

const DAY_LABELS = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];
const MONTH_LABELS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

// Duolingo-style month grid: 7 columns starting Sunday, blanks padding the
// first week out to the correct weekday.
function buildMonthGrid(year, month) {
  const first = new Date(year, month, 1);
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const cells = [];
  for (let i = 0; i < first.getDay(); i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);
  return cells;
}

function pad2(n) { return String(n).padStart(2, '0'); }

export default function StreakCalendarModal({ onClose, lastStudied: lastStudiedProp }) {
  const [cursor, setCursor] = useState(() => {
    const now = new Date();
    return { year: now.getFullYear(), month: now.getMonth() };
  });
  const [cache, setCache] = useState({});     // { 'YYYY-MM': { streak, lastStudied, activeDates } }
  const [loading, setLoading] = useState(true);

  const key = `${cursor.year}-${pad2(cursor.month + 1)}`;
  const entry = cache[key];
  const today = new Date();
  const isCurrentMonth = cursor.year === today.getFullYear() && cursor.month === today.getMonth();
  const canGoForward = !isCurrentMonth;

  const load = useCallback((year, month) => {
    const k = `${year}-${pad2(month + 1)}`;
    setLoading(true);
    getStreakCalendar({ month: k })
      .then(res => setCache(prev => ({ ...prev, [k]: res.data })))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (!cache[key]) load(cursor.year, cursor.month);
    else setLoading(false);
  }, [key, cache, cursor, load]);

  useEffect(() => {
    const onKey = e => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const activeSet = useMemo(() => new Set(entry?.activeDates || []), [entry]);
  const cells = useMemo(() => buildMonthGrid(cursor.year, cursor.month), [cursor]);
  const streak = entry?.streak ?? '…';
  const lastStudied = entry?.lastStudied ?? lastStudiedProp;
  const doneToday = isStudiedToday(lastStudied);

  const goPrev = () => setCursor(({ year, month }) => (
    month === 0 ? { year: year - 1, month: 11 } : { year, month: month - 1 }
  ));
  const goNext = () => {
    if (!canGoForward) return;
    setCursor(({ year, month }) => (
      month === 11 ? { year: year + 1, month: 0 } : { year, month: month + 1 }
    ));
  };

  const todayStr = `${today.getFullYear()}-${pad2(today.getMonth() + 1)}-${pad2(today.getDate())}`;

  return createPortal(
    <div className="modal-overlay streak-cal-overlay" onClick={onClose}>
      <div className="modal-box streak-cal-box" onClick={e => e.stopPropagation()}>
        <button className="btn-icon streak-cal-close" onClick={onClose} aria-label="Close">
          <RiCloseLine size={18} />
        </button>

        <div className="streak-cal-header">
          <div className={`streak-cal-flame${doneToday ? ' done' : ''}`}>
            {doneToday ? <RiFireFill size={30} /> : <RiFireLine size={30} />}
          </div>
          <div className="streak-cal-count">{streak}</div>
          <div className="streak-cal-label">day streak</div>
          <div className={`streak-cal-status${doneToday ? ' done' : ''}`}>
            {doneToday ? "Today's streak is done" : 'Study today to keep it going'}
          </div>
          {/* Asleep until the day is earned, then wide awake. A quieter
              signal than the copy, and the one people will read first. */}
          <div style={{ display: 'flex', justifyContent: 'center', marginTop: 12 }}>
            <KitsuMark
              size={46}
              animation={doneToday ? 'breathe' : 'sleep'}
              interactive={false}
            />
          </div>
        </div>

        <div className="streak-cal-nav">
          <button type="button" onClick={goPrev} aria-label="Previous month"><RiArrowLeftSLine size={18} /></button>
          <span>{MONTH_LABELS[cursor.month]} {cursor.year}</span>
          <button type="button" onClick={goNext} disabled={!canGoForward} aria-label="Next month"><RiArrowRightSLine size={18} /></button>
        </div>

        <div className="streak-cal-weekdays">
          {DAY_LABELS.map((d, i) => <span key={i}>{d}</span>)}
        </div>

        <div className={`streak-cal-grid${loading ? ' is-loading' : ''}`}>
          {cells.map((d, i) => {
            if (d === null) return <span key={`b${i}`} className="streak-cal-cell empty" />;
            const dateStr = `${cursor.year}-${pad2(cursor.month + 1)}-${pad2(d)}`;
            const active = activeSet.has(dateStr);
            const isToday = dateStr === todayStr;
            const isFuture = dateStr > todayStr;
            return (
              <span key={dateStr} className={`streak-cal-cell${active ? ' active' : ''}${isToday ? ' today' : ''}${isFuture ? ' future' : ''}`}>
                <span className="streak-cal-cell-icon">
                  {active ? <RiFireFill size={14} /> : <RiFireLine size={12} />}
                </span>
                <span className="streak-cal-cell-num">{d}</span>
              </span>
            );
          })}
        </div>
      </div>
    </div>,
    document.body
  );
}
